import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Card,
    Typography,
    Grid,
    Box,
    IconButton,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/autoplay";
import { Autoplay } from "swiper/modules";
import { useSelector } from "react-redux";
import { investorApi } from "../../api/api";
import GradientButton from "../common/GradiantButton";
import BrandPagination from "../common/BrandPagination";
import { useNavigate } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ShareIcon from "@mui/icons-material/Share";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from '@mui/icons-material/Favorite';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useDispatch } from "react-redux";
import { toggleFavorite } from "../../redux/reducers/favoritesSlice";
import ShareProperty from "../common/shareProperty";
import Loader from "../common/Loader";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LoginModal from "../common/LoginModal";
import { useTheme, useMediaQuery, Divider } from "@mui/material";
import { formatIndianCurrencyWithSymbol, resolvePropertyPrice } from "../../utils/priceFormat";

const ITEMS_PER_PAGE = 25;

const PropertyCard = ({
    filters,
    SelectLocation,
    keyword,
    showAd,
    isAuthenticated,
    searchData
}) => {
    const themeMui = useTheme();
    const isMobile = useMediaQuery(themeMui.breakpoints.down("sm"));
    const { user_refferal_code, userId } = useSelector((state) => state.user);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const favoriteItems = useSelector((state) => state.favorites.items);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const theme = useSelector((data) => data.themeValue.colors);
    const [showLoginModal, setShowLoginModal] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);

    const {
        address,
        city,
        type,
        minPrice,
        maxPrice,
        minSqft,
        maxSqft,
    } = searchData || {};

    useEffect(() => {
        const fetchFilteredData = async () => {
            try {
                setLoading(true);

                const payload = {
                    city: searchData?.city || null,
                    address: searchData?.address || null,
                    type: searchData?.type || filters?.type || null,

                    minPrice:
                        searchData?.minPrice ??
                        filters?.marketPriceRange?.[0] ??
                        null,

                    maxPrice:
                        searchData?.maxPrice ??
                        filters?.marketPriceRange?.[1] ??
                        null,

                    minSqft:
                        searchData?.minSqft ??
                        filters?.sizeRange?.[0] ??
                        null,

                    maxSqft:
                        searchData?.maxSqft ??
                        filters?.sizeRange?.[1] ??
                        null,

                    keyword: keyword || null
                };

                const response = await investorApi.filterProperties(payload);
                setProperties(response.data.data);

                // Reset to page 1 whenever new data is fetched
                setCurrentPage(1);
            } catch (error) {
                console.error("Filter error:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchFilteredData();
    }, [searchData, filters, keyword]);

    // Derived pagination values
    const totalPages = Math.ceil(properties.length / ITEMS_PER_PAGE);
    const paginatedProperties = properties.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        // Scroll to top of list on page change
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const generateSlug = (property) => {
        const titlePart = `${property.name}-${property.city}`
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-")
            .trim();

        return `${titlePart}-${property.id}`;
    };

    const handleViewDetails = (property) => {
        const slug = generateSlug(property);

        let basePath = "";

        if (property.type === "property") {
            basePath = `/property/${slug}`;
        } else if (property.type === "project") {
            basePath = `/project/${slug}`;
        } else if (property.type === "villa") {
            basePath = `/villa/${slug}`;
        } else if (property.type === "apartment") {
            basePath = `/apartment/${slug}`;
        }

        if (isAuthenticated && user_refferal_code) {
            basePath += `?ref=${user_refferal_code}`;
        }

        window.open(basePath, "_blank");
    };

    if (loading) {
        return <Loader />;
    }

    const getUniqueId = (property) => `${property.type}_${property.id}`;

    const toggleLike = (property) => {
        const uniqueId = getUniqueId(property);
        dispatch(toggleFavorite(uniqueId));
    };

    const isLiked = (property) => {
        const uniqueId = getUniqueId(property);
        return favoriteItems.includes(uniqueId);
    };

    const getShareUrl = (property) => {
        const baseUrl = window.location.origin;
        const slug = generateSlug(property);

        let path = "";

        if (property.type === "property") {
            path = `property/${slug}`;
        } else if (property.type === "project") {
            path = `project/${slug}`;
        } else if (property.type === "villa") {
            path = `villa/${slug}`;
        } else if (property.type === "apartment") {
            path = `apartment/${slug}`;
        }

        if (user_refferal_code) {
            path += `?ref=${user_refferal_code}`;
        }

        return `${baseUrl}/${path}`;
    };

    return (
        <Box sx={{ maxWidth: "100%", mx: "auto", marginTop: "15px" }}>
            {paginatedProperties.length > 0 ? (
                <>
                    {paginatedProperties.map((property) => (
                        <Card key={`${property.id}-${property.type}`} sx={{
                            mb: { xs: 0, sm: 2 },
                            boxShadow: `0px 4px 20px ${"rgba(0,0,0,0.2)"}`,
                            marginTop: "20px",
                            marginBottom: { xs: "20px" }
                        }}>
                            <Box sx={{ p: 1, backgroundColor: '#f7f6f6ff' }}>
                                <Typography
                                    variant="h6"
                                    mb={1}
                                    onClick={() => handleViewDetails(property)}
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'color 0.3s ease',
                                        '&:hover': {
                                            color: 'primary.main',
                                        },
                                    }}
                                >
                                    {property.name}
                                    <OpenInNewIcon sx={{ fontSize: 20, paddingTop: "5px", color: theme.normal_iconS }} />
                                </Typography>
                            </Box>

                            <Box sx={{ px: 2, pt: 1, backgroundColor: theme.background }}>
                                <Grid container alignItems="center" spacing={1} sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Grid sx={{ display: "flex", alignItems: "start" }}>
                                        <LocationOnIcon style={{ color: theme.normal_icon }} />
                                        <Grid xs>
                                            <Typography variant="body1" fontWeight="medium" sx={{ color: theme.text }}>
                                                {property.address || "N/A"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ color: theme.text }}>
                                                {property.city || "N/A"}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid sx={{ textAlign: "end", alignItems: "end" }}>
                                        <Typography
                                            sx={{
                                                backgroundColor:
                                                    property.type === "project" ? "#e3f2fd" : "#e8f5e9",
                                                color: "#333",
                                                padding: "4px 10px",
                                                borderRadius: "12px",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {property.type}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Grid container spacing={2} alignItems="stretch" sx={{ px: 1, pb: 2, pt: "5px", backgroundColor: theme.background }}>
                                <Grid sx={{
                                    height: showAd ? "150px" : "180px",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                    width: { xs: "95%", sm: "47%" },
                                    position: 'relative',
                                    maxWidth: { xs: "17rem", sm: "16rem" },
                                }}>
                                    <Swiper
                                        modules={[Autoplay]}
                                        autoplay={{ delay: 2000, disableOnInteraction: false }}
                                        loop={true}
                                        spaceBetween={10}
                                        slidesPerView={1}
                                        style={{ width: '100%', height: '100%', margin: 0, padding: 0 }}
                                    >
                                        {(property.images || []).map((url, index) => (
                                            <SwiperSlide key={index} style={{ width: '100%', height: '100%' }}>
                                                <img src={url} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </Grid>

                                <Grid sx={{
                                    height: showAd ? "150px" : "180px",
                                    color: "black",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    width: { xs: "100%", sm: "50%" }
                                }}>
                                    <Box sx={{
                                        height: { xs: "auto", sm: "100px" },
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-around",
                                        width: { xs: "100%", sm: "100%" },
                                        px: 2,
                                    }}>
                                        {property.type === "project" ? (
                                            <>
                                                <Box display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="subtitle2" sx={{ color: theme.text }}>Total Plots</Typography>
                                                    <Typography variant="body1" fontWeight="medium" sx={{ color: theme.text }}>{property.Numberofplots || "N/A"}</Typography>
                                                </Box>
                                                <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem sx={{ backgroundColor: theme.text, mx: isMobile ? 0 : 1, my: isMobile ? 1 : 0 }} />
                                                <Box display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="subtitle2" sx={{ color: theme.text }}>Available Plots</Typography>
                                                    <Typography variant="body1" fontWeight="medium" sx={{ color: theme.text }}>{property.AvailablePlots ?? "N/A"}</Typography>
                                                </Box>
                                            </>
                                        ) : property.type === "apartment" ? (
                                            <>
                                                <Box display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="subtitle2" sx={{ color: theme.text }}>Total Flats</Typography>
                                                    <Typography variant="body1" fontWeight="medium" sx={{ color: theme.text }}>{property.TotalUnits || "N/A"}</Typography>
                                                </Box>
                                                <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem sx={{ backgroundColor: theme.text, mx: isMobile ? 0 : 1, my: isMobile ? 1 : 0 }} />
                                                <Box display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="subtitle2" sx={{ color: theme.text }}>Available Flats</Typography>
                                                    <Typography variant="body1" fontWeight="medium" sx={{ color: theme.text }}>{property.AvailableUnits ?? "N/A"}</Typography>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                {property.sqftPrice && (
                                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                                        <Typography variant="subtitle2" sx={{ color: theme.text }}>Sqft Price</Typography>
                                                        <Typography variant="body1" fontWeight="medium" sx={{ color: theme.text }}>
                                                          {formatIndianCurrencyWithSymbol(property.sqftPrice) || "—"}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {resolvePropertyPrice(property) != null && (
                                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                                        <Typography variant="subtitle2" sx={{ color: theme.text }}>Total Price</Typography>
                                                        <Typography variant="body1" fontWeight="medium" sx={{ color: theme.text }}>
                                                          {formatIndianCurrencyWithSymbol(resolvePropertyPrice(property))}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem sx={{ backgroundColor: theme.text, mx: isMobile ? 0 : 1, my: isMobile ? 1 : 0 }} />
                                                <Box display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="subtitle2" sx={{ color: theme.text }}>Size (SqFt)</Typography>
                                                    <Typography variant="body1" fontWeight="medium" sx={{ color: theme.text }}>{property.SizeInSqFt || "N/A"}</Typography>
                                                </Box>
                                            </>
                                        )}
                                    </Box>

                                    <Box sx={{ height: "50px", display: "flex", justifyContent: "space-evenly", width: '100%' }}>
                                        <GradientButton text="View Details" onClick={() => handleViewDetails(property)} width="90%" height={45} style={{ borderRadius: "10px" }} />

                                        {/* LIKE BUTTON */}
                                        <IconButton
                                            sx={{ border: `1px solid lightgray`, height: '38px', width: '38px', borderRadius: 0, padding: 0, mx: 1, mt: "2px" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isAuthenticated) {
                                                    setShowLoginModal(true);
                                                    return;
                                                }
                                                toggleLike(property);
                                            }}
                                        >
                                            {isLiked(property) ? (
                                                <FavoriteIcon style={{ fontSize: 20, color: "red" }} />
                                            ) : (
                                                <FavoriteBorderIcon style={{ color: theme.normal_icon }} />
                                            )}
                                        </IconButton>

                                        {/* SHARE BUTTON */}
                                        <IconButton
                                            sx={{ border: '1px solid lightgray', height: '38px', width: '38px', borderRadius: 0, padding: 0, mx: 1, mt: "2px" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isAuthenticated) {
                                                    setShowLoginModal(true);
                                                    return;
                                                }
                                                setSelectedProperty(property);
                                                setShowPopup(true);
                                            }}
                                        >
                                            <ShareIcon style={{ color: theme.normal_icon }} />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Card>
                    ))}

                    {/* ── Pagination Controls ── */}
                    {totalPages > 1 && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, my: 3 }}>
                            <Typography variant="body2" sx={{ color: theme.text }}>
                                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, properties.length)} of {properties.length} properties
                            </Typography>
                            <BrandPagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                showLabels
                            />
                        </Box>
                    )}
                </>
            ) : (
                <Box sx={{ height: "60vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.background, textAlign: "center", px: 2, my: 4 }}>
                    <Typography variant="h6" sx={{ color: theme.text }}>
                        {keyword || city || address || SelectLocation ? "No search results found." : "No properties available."}
                    </Typography>
                </Box>
            )}

            {/* Post Your Property CTA */}
            <Box style={{ backgroundColor: theme.background, padding: "20px", borderRadius: "8px", boxShadow: `0 0px 1px ${theme.text}`, marginBottom: "20px" }}>
                <Box sx={{ textAlign: "center" }}>
                    <p style={{ fontWeight: "500", color: theme.text, marginBottom: "10px" }}>Are you a Property Owner?</p>
                    <GradientButton text="Post Your Property" onClick={() => navigate("/AddProperty")} width={{ lg: "30%", xs: "70%" }} height={45} style={{ borderRadius: "10px" }} />
                </Box>
            </Box>

            {showPopup && selectedProperty && (
                <ShareProperty property={selectedProperty} getShareUrl={getShareUrl} onClose={() => setShowPopup(false)} />
            )}

            {showLoginModal && (
                <LoginModal onClose={() => setShowLoginModal(false)} />
            )}
        </Box>
    );
};

export default PropertyCard;
